import React from 'react';
import { formatPSTDate } from '../../utils/timezone';
import './DailySummary.css';

export interface DailySummaryData {
  id: number;
  date: string;
  title: string;
  summary: string;
  key_themes: string[];
  article_count: number;
  featured: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
  weekly_summary_id?: number;
}

export interface DailySummaryProps {
  dailySummary: DailySummaryData;
  showThemes?: boolean;
  showStats?: boolean;
  showShareButtons?: boolean;
  className?: string;
}

const DailySummary: React.FC<DailySummaryProps> = ({
  dailySummary,
  showThemes = true,
  showStats = true,
  showShareButtons = true,
  className = '',
}) => {
  const formatDate = (dateString: string) => {
    return formatPSTDate(dateString);
  };

  // Parse key_themes if it's a string
  const keyThemes: string[] = typeof dailySummary.key_themes === 'string' 
    ? JSON.parse(dailySummary.key_themes || '[]') 
    : dailySummary.key_themes || [];

  const getDailySlug = () => {
    const datePart = dailySummary.date?.split('T')[0] || dailySummary.date;
    return datePart || String(dailySummary.id);
  };

  const getDailySummaryUrl = () => `${window.location.origin}/daily/${getDailySlug()}`;

  const handleShare = (platform: string) => {
    const shareTarget = getDailySummaryUrl();
    const url = encodeURIComponent(shareTarget);
    const title = encodeURIComponent(`${dailySummary.title} - AI Business Inside`);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'instagram':
        // Instagram doesn't have direct URL sharing, so we'll copy to clipboard
        navigator.clipboard.writeText(shareTarget);
        alert('Link copied! You can now paste it in your Instagram story or bio.');
        return;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareTarget);
        return;
      default:
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className={`daily-summary ${dailySummary.featured ? 'daily-summary--featured' : ''} ${className}`}>
      {/* Header */}
      <header className="daily-summary__header">
        <div className="daily-summary__date-container">
          <time 
            className="daily-summary__date"
            dateTime={dailySummary.date}
            title={formatDate(dailySummary.date)}
          >
            {formatDate(dailySummary.date)}
          </time>
          {dailySummary.featured && (
            <span className="daily-summary__featured-badge">
              Featured
            </span>
          )}
        </div>
        
        <h2 className="daily-summary__title">
          {dailySummary.title}
        </h2>
      </header>

      {/* Summary Content */}
      <div className="daily-summary__content">
        <p className="daily-summary__text">
          {dailySummary.summary}
        </p>
      </div>

      {/* Key Themes */}
      {showThemes && keyThemes && keyThemes.length > 0 && (
        <div className="daily-summary__themes">
          <h3 className="daily-summary__themes-title">Key Themes:</h3>
          <div className="daily-summary__themes-list">
            {keyThemes.map((theme: string, index: number) => (
              <span key={index} className="daily-summary__theme">
                {theme}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer with Stats and Share */}
      <footer className="daily-summary__footer">
        {/* Stats */}
        {showStats && (
          <div className="daily-summary__stats">
            <span className="daily-summary__stat" title="Articles">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              {dailySummary.article_count} {dailySummary.article_count === 1 ? 'article' : 'articles'}
            </span>

            {dailySummary.view_count > 0 && (
              <span className="daily-summary__stat" title="Views">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {dailySummary.view_count} {dailySummary.view_count === 1 ? 'view' : 'views'}
              </span>
            )}
          </div>
        )}

        {/* Share Buttons */}
        {showShareButtons && (
          <div className="daily-summary__share">
            <span className="daily-summary__share-label">Share:</span>
            <div className="daily-summary__share-buttons">
              <button
                className="daily-summary__share-button"
                onClick={() => handleShare('x')}
                title="Share on X (formerly Twitter)"
                aria-label="Share on X"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </button>

              <button
                className="daily-summary__share-button"
                onClick={() => handleShare('instagram')}
                title="Share on Instagram"
                aria-label="Share on Instagram"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </button>

              <button
                className="daily-summary__share-button"
                onClick={() => handleShare('facebook')}
                title="Share on Facebook"
                aria-label="Share on Facebook"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>

              <button
                className="daily-summary__share-button"
                onClick={() => handleShare('linkedin')}
                title="Share on LinkedIn"
                aria-label="Share on LinkedIn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </button>

              <button
                className="daily-summary__share-button"
                onClick={() => handleShare('copy')}
                title="Copy link"
                aria-label="Copy link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
};

export default DailySummary;
